// KnowledgeBasePage.jsx
// Requiere: lucide-react
// Variable de entorno: VITE_API_URL (Vite) o REACT_APP_API_URL (CRA)

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Upload, FileText, Building2, Package, HelpCircle,
  Zap, ShieldCheck, RefreshCw, CheckCircle, X,
  Loader2, Inbox, AlertCircle, Database, Calendar,
  DollarSign, Tag, ToggleLeft, ToggleRight, Hash
} from "lucide-react";

/* ─── API ROOT ──────────────────────────────────────── */
const API_URL =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  process.env?.REACT_APP_API_URL ||
  "http://localhost:3000";

/* ─── EMBEDDED CSS ──────────────────────────────────── */
const SHEET = `
  .kb { --bg:#060a0f; --s1:#0b1422; --s2:#0e1a2d; --s3:#111f34;
    --bd:#192538; --bdh:#243650; --t1:#e4edf8; --t2:#6b859e; --t3:#2e4460;
    --ok:#22c55e; --err:#ef4444; --warn:#f59e0b; }

  .kb { min-height:100vh; background:var(--bg); color:var(--t1);
    padding:28px 0; font-family:ui-monospace,'Cascadia Code','Menlo',monospace;
    font-size:16px; width:85%; overflow-y:scroll}

  /* ── header ── */
  .kb-hd { width:95%; margin:0 auto 28px; display:flex; align-items:center;
    gap:14px; padding-bottom:22px; border-bottom:1px solid var(--bd); }
  .kb-hd-icon { width:42px; height:42px; border-radius:10px;
    background:linear-gradient(135deg,#0a1e34,#0d2a46);
    border:1px solid #1a3858; display:flex; align-items:center; justify-content:center;
    color:#4da8ea; flex-shrink:0; }
  .kb-hd h1 { margin:0; font-size:16px; font-weight:700; letter-spacing:-.01em; color:var(--t1); }
  .kb-hd p  { margin:3px 0 0; font-size:16px; color:var(--t3); }
  .kb-hd-tag { margin-left:auto; padding:4px 10px; border-radius:20px;
    background:var(--s2); border:1px solid var(--bd); font-size:16px;
    color:var(--t3); letter-spacing:.06em; text-transform:uppercase; }

  /* ── cards wrap ── */
  .kb-cards { width:95%; margin:0 auto; display:flex; flex-direction:column; gap:14px; }

  /* ── card ── */
  .kb-card { background:var(--s1); border:1px solid var(--bd);
    border-radius:12px; overflow:hidden; transition:border-color .2s; }
  .kb-card:hover { border-color:var(--bdh); }

  .kb-ch { display:flex; align-items:center; gap:10px; padding:13px 18px;
    border-bottom:1px solid var(--bd); background:var(--s2); }
  .kb-ch-ic { width:28px; height:28px; border-radius:6px; display:flex;
    align-items:center; justify-content:center; flex-shrink:0; }
  .kb-ch h2 { margin:0; font-size:16px; font-weight:600; color:var(--t1); flex:1; }
  .kb-ch p  { margin:0; font-size:16px; color:var(--t3); }
  .kb-ch-info { text-align:right; }

  .kb-body { display:flex; min-height:290px; }

  /* ── left panel ── */
  .kb-left { width:38%; padding:18px; border-right:1px solid var(--bd);
    display:flex; flex-direction:column; gap:12px; flex-shrink:0; }

  /* ── right panel ── */
  .kb-right { flex:1; display:flex; flex-direction:column; overflow:hidden;
    min-width:0; }
  .kb-rh { display:flex; align-items:center; justify-content:space-between;
    padding:9px 14px; border-bottom:1px solid var(--bd); background:var(--s2); flex-shrink:0; }
  .kb-rh-title { display:flex; align-items:center; gap:6px; font-size:16px;
    color:var(--t3); text-transform:uppercase; letter-spacing:.08em; }
  .kb-cnt { padding:1px 7px; background:var(--s3); border:1px solid var(--bd);
    border-radius:4px; font-size:16px; color:var(--t2); }
  .kb-scroll { flex:1; overflow-y:auto; padding:8px 10px;
    display:flex; flex-direction:column; gap:5px; }
  .kb-scroll::-webkit-scrollbar { width:3px; }
  .kb-scroll::-webkit-scrollbar-track { background:transparent; }
  .kb-scroll::-webkit-scrollbar-thumb { background:var(--s3); border-radius:4px; }
  .kb-scroll::-webkit-scrollbar-thumb:hover { background:var(--bdh); }

  /* ── item ── */
  .kb-item { background:var(--s2); border:1px solid var(--bd); border-radius:8px;
    padding:9px 11px; display:flex; flex-direction:column; gap:5px;
    transition:border-color .15s; }
  .kb-item:hover { border-color:var(--bdh); }
  .kb-it-top { display:flex; align-items:center; gap:6px; }
  .kb-it-id { font-size:96x; color:var(--t3); overflow:hidden; text-overflow:ellipsis;
    white-space:nowrap; flex:1; min-width:0; }
  .kb-it-pre { font-size:16px; color:var(--t2); line-height:1.5;
    display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
  .kb-it-foot { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }

  /* ── badge ── */
  .kb-badge { padding:2px 7px; border-radius:4px; font-size:96x;
    text-transform:uppercase; letter-spacing:.06em; font-weight:700;
    white-space:nowrap; flex-shrink:0; }

  /* ── form elements ── */
  .kb-lbl { font-size:16px; text-transform:uppercase; letter-spacing:.08em;
    color:var(--t3); margin-bottom:4px; display:block; }
  .kb-sel, .kb-inp, .kb-ta {
    width:100%; background:var(--s2); border:1px solid var(--bd); border-radius:7px;
    color:var(--t1); font-size:16px; font-family:inherit; padding:8px 10px;
    outline:none; transition:border-color .15s; box-sizing:border-box; }
  .kb-sel:focus, .kb-inp:focus, .kb-ta:focus { border-color:var(--bdh); }
  .kb-sel option { background:var(--s2); }
  .kb-ta { resize:vertical; min-height:72px; line-height:1.5; }
  .kb-row { display:flex; gap:8px; }
  .kb-row > * { flex:1; min-width:0; }

  /* ── dropzone ── */
  .kb-dz { border:1.5px dashed var(--bd); border-radius:9px; padding:22px 14px;
    text-align:center; cursor:pointer; transition:all .2s; background:var(--bg); }
  .kb-dz:hover, .kb-dz.drag { border-color:var(--ac,#00c8a8);
    background:color-mix(in srgb,var(--ac,#00c8a8) 6%,transparent); }
  .kb-dz.got { border-style:solid;
    border-color:color-mix(in srgb,var(--ac,#00c8a8) 45%,transparent);
    background:color-mix(in srgb,var(--ac,#00c8a8) 5%,transparent); }
  .kb-dz-ic { width:34px; height:34px; border-radius:8px; display:flex;
    align-items:center; justify-content:center; margin:0 auto 8px; }
  .kb-dz p { margin:0; font-size:16px; }
  .kb-dz .sub { font-size:16px; color:var(--t3); margin-top:3px; }

  /* ── button ── */
  .kb-btn { width:100%; padding:9px 14px; border-radius:7px; border:none;
    font-size:16px; font-family:inherit; font-weight:700; cursor:pointer;
    display:flex; align-items:center; justify-content:center; gap:6px;
    letter-spacing:.04em; transition:opacity .15s, transform .1s; text-transform:uppercase; }
  .kb-btn:disabled { opacity:.35; cursor:not-allowed; transform:none !important; }
  .kb-btn:not(:disabled):hover { opacity:.85; transform:translateY(-1px); }
  .kb-btn:not(:disabled):active { transform:translateY(0); }

  .kb-rbtn { width:26px; height:26px; display:flex; align-items:center;
    justify-content:center; border-radius:5px; border:1px solid var(--bd);
    background:transparent; color:var(--t3); cursor:pointer; transition:all .15s; }
  .kb-rbtn:hover { background:var(--s3); color:var(--t2); border-color:var(--bdh); }

  /* ── empty / spinner ── */
  .kb-empty { display:flex; flex-direction:column; align-items:center;
    justify-content:center; flex:1; gap:8px; color:var(--t3); padding:32px 0; }
  .kb-empty span { font-size:16px; }
  .kb-spin { display:flex; align-items:center; justify-content:center;
    flex:1; padding:32px 0; color:var(--t3); }

  /* ── toast ── */
  .kb-toast { position:fixed; bottom:22px; right:22px; z-index:9999;
    display:flex; align-items:flex-start; gap:10px; padding:12px 14px;
    border-radius:10px; font-size:16px; max-width:340px;
    box-shadow:0 8px 36px rgba(0,0,0,.55); animation:tin .25s ease; }
  @keyframes tin { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  .kb-toast.ok  { background:#081c12; border:1px solid #1a4228; color:#6ee7b7; }
  .kb-toast.err { background:#1c0808; border:1px solid #421a1a; color:#fca5a5; }
  .kb-toast-x { background:transparent; border:none; cursor:pointer; padding:0;
    color:inherit; opacity:.5; margin-left:auto; flex-shrink:0; line-height:1; }
  .kb-toast-x:hover { opacity:1; }

  /* ── status dot ── */
  .dot-on  { width:6px;height:6px;border-radius:50%;background:var(--ok);
    box-shadow:0 0 5px var(--ok);flex-shrink:0; }
  .dot-off { width:6px;height:6px;border-radius:50%;background:var(--t3);flex-shrink:0; }

  /* ── promo specific ── */
  .kb-promo-price { font-size:16px; font-weight:700; margin:0; }
`;

/* ─── UTILS ─────────────────────────────────────────── */
const fmt = (d) =>
  d ? new Date(d).toLocaleDateString("es", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const truncate = (str = "", n = 80) =>
  str.length > n ? str.slice(0, n) + "…" : str;

/* ─── TOAST ─────────────────────────────────────────── */
function Toast({ msg, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`kb-toast ${type === "ok" ? "ok" : "err"}`}>
      {type === "ok"
        ? <CheckCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
        : <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />}
      <span style={{ flex: 1 }}>{msg}</span>
      <button className="kb-toast-x" onClick={onClose}><X size={13} /></button>
    </div>
  );
}

/* ─── BADGE ─────────────────────────────────────────── */
function Badge({ label, bg = "#1a2840", color = "#6b859e" }) {
  return (
    <span className="kb-badge" style={{ background: bg, color }}>
      {label}
    </span>
  );
}

/* ─── FILE DROP ZONE ────────────────────────────────── */
function DropZone({ onFile, file, loading, accent = "#00c8a8" }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const validate = useCallback((f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".md")) {
      alert("⚠️  Solo se permiten archivos .md (Markdown)");
      return;
    }
    onFile(f);
  }, [onFile]);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    validate(e.dataTransfer.files[0]);
  };

  const cls = ["kb-dz", drag ? "drag" : "", file ? "got" : ""].filter(Boolean).join(" ");

  return (
    <div
      className={cls}
      style={{ "--ac": accent }}
      onClick={() => !loading && inputRef.current?.click()}
      onDrop={onDrop}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".md"
        style={{ display: "none" }}
        onChange={(e) => validate(e.target.files[0])}
      />
      <div
        className="kb-dz-ic"
        style={{
          background: file
            ? `color-mix(in srgb, ${accent} 15%, transparent)`
            : "var(--s3)",
          color: file ? accent : "var(--t3)",
        }}
      >
        {file ? <FileText size={18} /> : <Upload size={18} />}
      </div>
      {file ? (
        <>
          <p style={{ color: accent, fontWeight: 600 }}>{truncate(file.name, 32)}</p>
          <p className="sub">Click para cambiar archivo</p>
        </>
      ) : (
        <>
          <p style={{ color: "var(--t2)" }}>Arrastra tu archivo .md aquí</p>
          <p className="sub">o haz click para seleccionar</p>
        </>
      )}
    </div>
  );
}

/* ─── ITEMS LIST (right panel) ──────────────────────── */
function ItemsList({ items, loading, onRefresh, renderItem }) {
  return (
    <>
      <div className="kb-rh">
        <div className="kb-rh-title">
          <Database size={11} />
          <span>Base vectorial</span>
          {items.length > 0 && (
            <span className="kb-cnt">{items.length}</span>
          )}
        </div>
        <button
          className="kb-rbtn"
          onClick={onRefresh}
          disabled={loading}
          title="Actualizar lista"
        >
          <RefreshCw size={12} className={loading ? "spin" : ""} style={loading ? { animation: "spin 1s linear infinite" } : {}} />
        </button>
      </div>
      <div className="kb-scroll">
        {loading ? (
          <div className="kb-spin"><Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /></div>
        ) : items.length === 0 ? (
          <div className="kb-empty">
            <Inbox size={22} />
            <span>Sin datos en la colección</span>
          </div>
        ) : (
          items.map((item, i) => (
            <div key={item.id ?? i} className="kb-item">
              {renderItem(item)}
            </div>
          ))
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </>
  );
}



/* ══════════════════════════════════════════════════════
   CARD 0 — TODO
══════════════════════════════════════════════════════ */
function TodoCard({ notify }) {
  const ACCENT = "#f5a623";

  const [file, setFile]       = useState(null);
  const [nombre, setNombre]   = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems]     = useState([]);
  const [loadingItems, setLI] = useState(false);

  const fetchItems = async () => {
    setLI(true);
    try {
      const r = await fetch(`${API_URL}ingestion/generic/items`);
      const d = await r.json();
      console.log("[fetchItems] Respuesta del servidor:", d);
      setItems(d.data ?? []);
    } catch (error) {
      console.error("[fetchItems] Error al recuperar los items:", error);
      setItems([]);
    } finally {
      setLI(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSubmit = async () => {
    if (!file) return notify("Selecciona un archivo .md", "err");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      fd.append("nombre", nombre);
      const r = await fetch(`${API_URL}ingestion/generic/importar`, { method: "POST", body: fd });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error ?? "Error al importar");
      notify(d.mensaje ?? "✓ Todos importados correctamente", "ok");
      setFile(null);
      fetchItems();
    } catch (e) { notify(e.message, "err"); }
    finally { setLoading(false); }
  };

  return (
    <div className="kb-card">
      <div className="kb-ch">
        <div className="kb-ch-ic" style={{ background: `color-mix(in srgb,${ACCENT} 12%,transparent)`, color: ACCENT }}>
          <Package size={15} />
        </div>
        <div style={{ flex: 1 }}>
          <h2>TODO</h2>
          <p>Todos los Chunks</p>
        </div>
        <div className="kb-badge" style={{ background: `color-mix(in srgb,${ACCENT} 12%,transparent)`, color: ACCENT }}>
          Todo
        </div>
      </div>
      <div className="kb-body">
        <div className="kb-left">
          <div style={{ padding: "10px 12px", background: "var(--s3)", borderRadius: 8, border: "1px solid var(--bd)" }}>
            <p style={{ margin: 0, fontSize: 11, color: "var(--t2)", lineHeight: 1.6 }}>
              Usa <code style={{ color: ACCENT }}>---</code> para separar secciones
              de productos en el archivo. Cada sección se procesa como un chunk independiente.
            </p>
          </div>

          <div>
            <label className="kb-lbl">Nombre del dataset</label>
            <input
              className="kb-inp"
              type="text"
              placeholder="Ej: catálogo completo, info general, etc."
              value={nombre}
              onChange={e => setNombre(e.target.value)}
              disabled={loading}
            />  
          </div>

          <DropZone onFile={setFile} file={file} loading={loading} accent={ACCENT} />
          <button
            className="kb-btn"
            style={{ background: ACCENT, color: "#0f0700" }}
            onClick={handleSubmit}
            disabled={loading || !file}
          >
            {loading
              ? <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> Procesando...</>
              : <><Upload size={13} /> Importar Catálogo</>}
          </button>
        </div>
        <div className="kb-right">
          <ItemsList
            items={items}
            loading={loadingItems}
            onRefresh={fetchItems}
            renderItem={(item) => (
              <>
                <div className="kb-it-top">
                  <Badge label={item.metadata?.categoria ?? "producto"} bg={`color-mix(in srgb,${ACCENT} 12%,transparent)`} color={ACCENT} />
                  <Badge label={item.metadata?.subcategoria ?? "—"}
                    bg={`color-mix(in srgb,${ACCENT} 12%,transparent)`} color={"white"} />
                  {item.metadata?.disponible === false && (
                    <Badge label="inactivo" bg="#1a1a1a" color="var(--t3)" />
                  )}
                  <span className="kb-it-id">{item.id}</span>
                </div>
                <p className="kb-it-pre" style={{ fontWeight: 500, color: "var(--t1)" }}>
                  {/* {item.metadata?.nombre ?? truncate(item.document_preview ?? "")} */}
                  {item.metadata?.nombre ?? item.document_preview ?? ""}
                </p>
                <div className="kb-it-foot">
                  {item.metadata?.precio > 0 && (
                    <Badge label={`$${item.metadata.precio.toLocaleString()}`}
                      bg="color-mix(in srgb,#22c55e 10%,transparent)" color="#4ade80" />
                  )}
                  <span className="kb-it-id">{fmt(item.metadata?.fecha_carga)}</span>
                </div>
              </>
            )}
          />
        </div>
      </div>
    </div>
  );
}



/* ══════════════════════════════════════════════════════
   CARD 1 — INFORMACIÓN DE LA EMPRESA
══════════════════════════════════════════════════════ */
function EmpresaCard({ notify }) {
  const ACCENT = "#00c8a8";
  const SUBCATS = ["general", "horarios", "ubicacion", "mision", "historia", "contacto", "otro"];

  const [file, setFile]           = useState(null);
  const [subcat, setSubcat]       = useState("general");
  const [loading, setLoading]     = useState(false);
  const [items, setItems]         = useState([]);
  const [loadingItems, setLI]     = useState(false);

  const fetchItems = async () => {
    setLI(true);
    try {
      const r = await fetch(`${API_URL}/api/empresa/items`);
      const d = await r.json();
      setItems(d.data ?? []);
    } catch { setItems([]); }
    finally { setLI(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSubmit = async () => {
    if (!file) return notify("Selecciona un archivo .md", "err");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      fd.append("subcategoria", subcat);
      const r = await fetch(`${API_URL}/api/empresa/importar`, { method: "POST", body: fd });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error ?? "Error al importar");
      notify(d.mensaje ?? `✓ ${d.detalle?.chunks ?? ""} chunks importados`, "ok");
      setFile(null);
      fetchItems();
    } catch (e) { notify(e.message, "err"); }
    finally { setLoading(false); }
  };

  return (
    <div className="kb-card">
      <div className="kb-ch">
        <div className="kb-ch-ic" style={{ background: `color-mix(in srgb,${ACCENT} 12%,transparent)`, color: ACCENT }}>
          <Building2 size={15} />
        </div>
        <div style={{ flex: 1 }}>
          <h2>Información de la Empresa</h2>
          <p>Horarios · Ubicación · Misión · Historia · Contacto</p>
        </div>
        <div className="kb-ch-info">
          <div className="kb-badge" style={{ background: `color-mix(in srgb,${ACCENT} 12%,transparent)`, color: ACCENT }}>
            empresa
          </div>
        </div>
      </div>
      <div className="kb-body">
        <div className="kb-left">
          <div>
            <label className="kb-lbl">Subcategoría</label>
            <select className="kb-sel" value={subcat} onChange={e => setSubcat(e.target.value)}>
              {SUBCATS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <DropZone onFile={setFile} file={file} loading={loading} accent={ACCENT} />
          <button
            className="kb-btn"
            style={{ background: ACCENT, color: "#050f0c" }}
            onClick={handleSubmit}
            disabled={loading || !file}
          >
            {loading
              ? <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> Importando...</>
              : <><Upload size={13} /> Importar a ChromaDB</>}
          </button>
        </div>
        <div className="kb-right">
          <ItemsList
            items={items}
            loading={loadingItems}
            onRefresh={fetchItems}
            renderItem={(item) => (
              <>
                <div className="kb-it-top">
                  <Badge label={item.metadata?.subcategoria ?? "—"} bg={`color-mix(in srgb,${ACCENT} 12%,transparent)`} color={ACCENT} />
                  <span className="kb-it-id">{item.id}</span>
                </div>
                <p className="kb-it-pre">{truncate(item.document_preview ?? item.document ?? "")}</p>
                <div className="kb-it-foot">
                  <span className="kb-it-id">{fmt(item.metadata?.fecha_carga)}</span>
                  <span className="kb-it-id">v{item.metadata?.version ?? "—"}</span>
                </div>
              </>
            )}
          />
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   CARD 2 — PRODUCTOS Y SERVICIOS
══════════════════════════════════════════════════════ */
function ProductosCard({ notify }) {
  const ACCENT = "#f5a623";

  const [file, setFile]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [items, setItems]     = useState([]);
  const [loadingItems, setLI] = useState(false);

  const fetchItems = async () => {
    setLI(true);
    try {
      const r = await fetch(`${API_URL}ingestion/productos/items`);
      const d = await r.json();
      console.log("[fetchItems] Respuesta del servidor:", d);
      console.log("[fetchItems] Items recibidos:", d.data);
      setItems(d.data ?? []);
    } catch (error) {
      console.error("[fetchItems] Error al recuperar los items:", error);
      setItems([]);
    } finally {
      setLI(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSubmit = async () => {
    if (!file) return notify("Selecciona un archivo .md", "err");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      const r = await fetch(`${API_URL}ingestion/productos/importar`, { method: "POST", body: fd });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error ?? "Error al importar");
      notify(d.mensaje ?? "✓ Productos importados correctamente", "ok");
      setFile(null);
      fetchItems();
    } catch (e) { notify(e.message, "err"); }
    finally { setLoading(false); }
  };

  return (
    <div className="kb-card">
      <div className="kb-ch">
        <div className="kb-ch-ic" style={{ background: `color-mix(in srgb,${ACCENT} 12%,transparent)`, color: ACCENT }}>
          <Package size={15} />
        </div>
        <div style={{ flex: 1 }}>
          <h2>Productos y Servicios</h2>
          <p>Catálogo · Precios · Planes · Características</p>
        </div>
        <div className="kb-badge" style={{ background: `color-mix(in srgb,${ACCENT} 12%,transparent)`, color: ACCENT }}>
          productos
        </div>
      </div>
      <div className="kb-body">
        <div className="kb-left">
          <div style={{ padding: "10px 12px", background: "var(--s3)", borderRadius: 8, border: "1px solid var(--bd)" }}>
            <p style={{ margin: 0, fontSize: 11, color: "var(--t2)", lineHeight: 1.6 }}>
              Usa <code style={{ color: ACCENT }}>---</code> para separar secciones
              de productos en el archivo. Cada sección se procesa como un chunk independiente.
            </p>
          </div>
          <DropZone onFile={setFile} file={file} loading={loading} accent={ACCENT} />
          <button
            className="kb-btn"
            style={{ background: ACCENT, color: "#0f0700" }}
            onClick={handleSubmit}
            disabled={loading || !file}
          >
            {loading
              ? <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> Procesando...</>
              : <><Upload size={13} /> Importar Catálogo</>}
          </button>
        </div>
        <div className="kb-right">
          <ItemsList
            items={items}
            loading={loadingItems}
            onRefresh={fetchItems}
            renderItem={(item) => (
              <>
                <div className="kb-it-top">
                  <Badge label={item.metadata?.categoria ?? "producto"} bg={`color-mix(in srgb,${ACCENT} 12%,transparent)`} color={ACCENT} />
                  <Badge label={item.metadata?.subcategoria ?? "—"}
                    bg={`color-mix(in srgb,${ACCENT} 12%,transparent)`} color={"white"} />
                  {item.metadata?.disponible === false && (
                    <Badge label="inactivo" bg="#1a1a1a" color="var(--t3)" />
                  )}
                  <span className="kb-it-id">{item.id}</span>
                </div>
                <p className="kb-it-pre" style={{ fontWeight: 500, color: "var(--t1)" }}>
                  {/* {item.metadata?.nombre ?? truncate(item.document_preview ?? "")} */}
                  {item.metadata?.nombre ?? item.document_preview ?? ""}
                </p>
                <div className="kb-it-foot">
                  {item.metadata?.precio > 0 && (
                    <Badge label={`$${item.metadata.precio.toLocaleString()}`}
                      bg="color-mix(in srgb,#22c55e 10%,transparent)" color="#4ade80" />
                  )}
                  <span className="kb-it-id">{fmt(item.metadata?.fecha_carga)}</span>
                </div>
              </>
            )}
          />
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   CARD 3 — FAQ
══════════════════════════════════════════════════════ */
function FaqCard({ notify }) {
  const ACCENT = "#a78bfa";
  const SUBCATS = ["clases_practicas", "documentacion", "duracion", "examenes_medicos", "horarios", "tramites", "multas", "otras", "pagos", "runt"];

  const [file, setFile]       = useState(null);
  const [subcat, setSubcat]   = useState("clases_practicas");
  const [loading, setLoading] = useState(false);
  const [items, setItems]     = useState([]);
  const [loadingItems, setLI] = useState(false);

  const fetchItems = async () => {
    setLI(true);
    try {
      const r = await fetch(`${API_URL}ingestion/faq/items`);
      const d = await r.json();
      setItems(d.data ?? []);
    } catch { setItems([]); }
    finally { setLI(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSubmit = async () => {
    if (!file) return notify("Selecciona un archivo .md", "err");
    setLoading(true);
    try {
      
      const fd = new FormData();
      fd.append("archivo", file);
      fd.append("subcategoria", subcat);
      const r = await fetch(`${API_URL}ingestion/faq/importar`, { method: "POST", body: fd });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error ?? "Error al importar");
      notify(d.mensaje ?? `✓ ${d.detalle?.total_faqs ?? ""} pares Q&A importados`, "ok");
      setFile(null);
      fetchItems();
    } catch (e) { notify(e.message, "err"); }
    finally { setLoading(false); }
  };

  return (
    <div className="kb-card">
      <div className="kb-ch">
        <div className="kb-ch-ic" style={{ background: `color-mix(in srgb,${ACCENT} 12%,transparent)`, color: ACCENT }}>
          <HelpCircle size={15} />
        </div>
        <div style={{ flex: 1 }}>
          <h2>Preguntas y Respuestas (FAQ)</h2>
          <p>Formato: ## Pregunta: … / Respuesta: … — cada par es un chunk</p>
        </div>
        <div className="kb-badge" style={{ background: `color-mix(in srgb,${ACCENT} 12%,transparent)`, color: ACCENT }}>
          faq
        </div>
      </div>
      <div className="kb-body">
        <div className="kb-left">
          <div>
            <label className="kb-lbl">Subcategoría</label>
            <select className="kb-sel" value={subcat} onChange={e => setSubcat(e.target.value)}>
              {SUBCATS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <DropZone onFile={setFile} file={file} loading={loading} accent={ACCENT} />
          <button
            className="kb-btn"
            style={{ background: ACCENT, color: "#07020f" }}
            onClick={handleSubmit}
            disabled={loading || !file}
          >
            {loading
              ? <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> Procesando...</>
              : <><Upload size={13} /> Importar FAQ</>}
          </button>
        </div>
        <div className="kb-right">
          <ItemsList
            items={items}
            loading={loadingItems}
            onRefresh={fetchItems}
            renderItem={(item) => (
              <>
                <div className="kb-it-top">
                  <Badge label={item.metadata?.categoria ?? "general"}
                    bg={`color-mix(in srgb,${ACCENT} 12%,transparent)`} color={ACCENT} />
                  <Badge label={item.metadata?.subcategoria ?? "—"}
                    bg={`color-mix(in srgb,${ACCENT} 12%,transparent)`} color={"white"} />
                  <span className="kb-it-id">{item.id}</span>
                </div>
                <p className="kb-it-pre">{truncate(item.document_preview ?? item.document ?? "")}</p>
                <div className="kb-it-foot">
                  <span className="kb-it-id">{fmt(item.metadata?.fecha_carga)}</span>
                </div>
              </>
            )}
          />
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   CARD 4 — PROMOCIONES (formulario de texto)
══════════════════════════════════════════════════════ */
const PROMO_INIT = {
  nombre: "", descripcion: "", fecha_inicio: "", fecha_fin: "",
  tipo_promo: "porcentaje", valor_descuento: "", codigo: "", aplica_a: "todos"
};

function PromocionesCard({ notify }) {
  const ACCENT = "#f97316";

  const [form, setForm]         = useState(PROMO_INIT);
  const [loading, setLoading]   = useState(false);
  const [items, setItems]       = useState([]);
  const [loadingItems, setLI]   = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const fetchItems = async () => {
    setLI(true);
    try {
      const r = await fetch(`${API_URL}/api/promociones/activas`);
      const d = await r.json();
      setItems(d.data?.ids
        ? d.data.ids.map((id, i) => ({ id, metadata: d.data.metadatas?.[i] ?? {}, document: d.data.documents?.[i] ?? "" }))
        : (d.data ?? []));
    } catch { setItems([]); }
    finally { setLI(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSubmit = async () => {
    if (!form.nombre || !form.descripcion || !form.fecha_inicio || !form.fecha_fin)
      return notify("Completa los campos obligatorios", "err");
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/promociones/crear`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, valor_descuento: Number(form.valor_descuento) || 0 }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error ?? "Error al crear");
      notify(`✓ Promoción "${form.nombre}" creada`, "ok");
      setForm(PROMO_INIT);
      fetchItems();
    } catch (e) { notify(e.message, "err"); }
    finally { setLoading(false); }
  };

  const handleDeactivate = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/promociones/${id}/desactivar`, { method: "PATCH" });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error);
      notify("Promoción desactivada", "ok");
      fetchItems();
    } catch (e) { notify(e.message, "err"); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="kb-card">
      <div className="kb-ch">
        <div className="kb-ch-ic" style={{ background: `color-mix(in srgb,${ACCENT} 12%,transparent)`, color: ACCENT }}>
          <Zap size={15} />
        </div>
        <div style={{ flex: 1 }}>
          <h2>Promociones y Contenido Dinámico</h2>
          <p>Descuentos activos · Códigos · Vigencia · Alta prioridad en retrieval</p>
        </div>
        <div className="kb-badge" style={{ background: `color-mix(in srgb,${ACCENT} 12%,transparent)`, color: ACCENT }}>
          dinámica
        </div>
      </div>
      <div className="kb-body">
        <div className="kb-left" style={{ gap: 10 }}>
          <div>
            <label className="kb-lbl">Nombre de la promoción *</label>
            <input className="kb-inp" placeholder="Ej: Descuento de verano 2024"
              value={form.nombre} onChange={e => set("nombre", e.target.value)} />
          </div>
          <div>
            <label className="kb-lbl">Descripción *</label>
            <textarea className="kb-ta" placeholder="Describe la promoción tal como la leerá el chatbot..."
              value={form.descripcion} onChange={e => set("descripcion", e.target.value)}
              style={{ minHeight: 60 }} />
          </div>
          <div className="kb-row">
            <div>
              <label className="kb-lbl">Tipo</label>
              <select className="kb-sel" value={form.tipo_promo} onChange={e => set("tipo_promo", e.target.value)}>
                <option value="porcentaje">Porcentaje %</option>
                <option value="monto_fijo">Monto fijo</option>
                <option value="2x1">2×1</option>
                <option value="envio_gratis">Envío gratis</option>
              </select>
            </div>
            <div>
              <label className="kb-lbl">Valor</label>
              <input className="kb-inp" type="number" placeholder="20"
                value={form.valor_descuento} onChange={e => set("valor_descuento", e.target.value)} />
            </div>
          </div>
          <div className="kb-row">
            <div>
              <label className="kb-lbl">Desde *</label>
              <input className="kb-inp" type="date" value={form.fecha_inicio}
                onChange={e => set("fecha_inicio", e.target.value)} />
            </div>
            <div>
              <label className="kb-lbl">Hasta *</label>
              <input className="kb-inp" type="date" value={form.fecha_fin}
                onChange={e => set("fecha_fin", e.target.value)} />
            </div>
          </div>
          <div className="kb-row">
            <div>
              <label className="kb-lbl">Código (opcional)</label>
              <input className="kb-inp" placeholder="PROMO2024"
                value={form.codigo} onChange={e => set("codigo", e.target.value.toUpperCase())} />
            </div>
            <div>
              <label className="kb-lbl">Aplica a</label>
              <input className="kb-inp" placeholder="todos / plan anual / ..."
                value={form.aplica_a} onChange={e => set("aplica_a", e.target.value)} />
            </div>
          </div>
          <button
            className="kb-btn"
            style={{ background: ACCENT, color: "#0f0500" }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading
              ? <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> Creando...</>
              : <><Zap size={13} /> Crear Promoción</>}
          </button>
        </div>
        <div className="kb-right">
          <ItemsList
            items={items}
            loading={loadingItems}
            onRefresh={fetchItems}
            renderItem={(item) => {
              const m = item.metadata ?? {};
              const vigente = m.fecha_fin >= today && m.activo !== false;
              return (
                <>
                  <div className="kb-it-top">
                    <div className={vigente ? "dot-on" : "dot-off"} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--t1)", flex: 1 }}>
                      {m.nombre ?? truncate(item.document ?? "", 40)}
                    </span>
                    {vigente && (
                      <button
                        onClick={() => handleDeactivate(item.id)}
                        title="Desactivar"
                        style={{ background: "transparent", border: "none", cursor: "pointer",
                          color: "var(--t3)", padding: 0, lineHeight: 1 }}
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>
                  <div className="kb-it-foot">
                    {m.tipo_promo && (
                      <Badge label={m.tipo_promo} bg={`color-mix(in srgb,${ACCENT} 12%,transparent)`} color={ACCENT} />
                    )}
                    {m.valor_descuento > 0 && (
                      <Badge label={m.tipo_promo === "porcentaje" ? `${m.valor_descuento}%` : `$${m.valor_descuento}`}
                        bg="color-mix(in srgb,#22c55e 10%,transparent)" color="#4ade80" />
                    )}
                    {m.codigo && (
                      <Badge label={m.codigo} bg="var(--s3)" color="var(--t2)" />
                    )}
                  </div>
                  <div className="kb-it-foot">
                    <span className="kb-it-id" style={{ fontSize: 10 }}>
                      <Calendar size={9} style={{ display: "inline", marginRight: 3 }} />
                      {fmt(m.fecha_inicio)} → {fmt(m.fecha_fin)}
                    </span>
                  </div>
                </>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   CARD 5 — REGLAS DEL CHATBOT
══════════════════════════════════════════════════════ */
function ReglasCard({ notify }) {
  const ACCENT = "#3b82f6";

  const [file, setFile]       = useState(null);
  const [empresaId]           = useState(1);
  const [loading, setLoading] = useState(false);
  const [items, setItems]     = useState([]);
  const [loadingItems, setLI] = useState(false);

  const fetchItems = async () => {
    setLI(true);
    try {
      const r = await fetch(`${API_URL}/api/reglas/${empresaId}`);
      const d = await r.json();
      // Puede ser { reglas_md } o array de reglas individuales
      if (d.data?.reglas_md) {
        // Parsear el markdown a líneas para mostrar
        const lineas = d.data.reglas_md
          .split("\n")
          .filter(l => l.trim().startsWith("-") || l.trim().startsWith("#"))
          .map((l, i) => ({ id: `regla_${i}`, document: l.trim(), metadata: { tipo: l.startsWith("#") ? "seccion" : "regla" } }));
        setItems(lineas);
      } else {
        setItems(d.data ?? []);
      }
    } catch { setItems([]); }
    finally { setLI(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSubmit = async () => {
    if (!file) return notify("Selecciona un archivo .md", "err");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("archivo", file);
      fd.append("empresa_id", empresaId);
      const r = await fetch(`${API_URL}/api/reglas/importar`, { method: "POST", body: fd });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error ?? "Error al importar");
      notify("✓ Reglas del chatbot actualizadas", "ok");
      setFile(null);
      fetchItems();
    } catch (e) { notify(e.message, "err"); }
    finally { setLoading(false); }
  };

  return (
    <div className="kb-card">
      <div className="kb-ch">
        <div className="kb-ch-ic" style={{ background: `color-mix(in srgb,${ACCENT} 12%,transparent)`, color: ACCENT }}>
          <ShieldCheck size={15} />
        </div>
        <div style={{ flex: 1 }}>
          <h2>Reglas del Chatbot</h2>
          <p>Identidad · Alcance · Flujo de ventas · Handoff al asesor · Restricciones</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="kb-badge" style={{ background: "var(--s3)", color: "var(--t3)" }}>
            BD relacional
          </div>
          <div className="kb-badge" style={{ background: `color-mix(in srgb,${ACCENT} 12%,transparent)`, color: ACCENT }}>
            system prompt
          </div>
        </div>
      </div>
      <div className="kb-body">
        <div className="kb-left">
          <div style={{ padding: "10px 12px", background: "var(--s3)", borderRadius: 8,
            border: `1px solid color-mix(in srgb,${ACCENT} 25%,transparent)` }}>
            <p style={{ margin: 0, fontSize: 11, color: "var(--t2)", lineHeight: 1.6 }}>
              Las reglas se guardan en la <strong style={{ color: ACCENT }}>base de datos relacional</strong>,
              no en ChromaDB. Se inyectan como system prompt al inicio de cada conversación.
            </p>
          </div>
          <div>
            <label className="kb-lbl">Empresa ID</label>
            <input className="kb-inp" value={empresaId} readOnly
              style={{ color: "var(--t3)", cursor: "default" }} />
          </div>
          <DropZone onFile={setFile} file={file} loading={loading} accent={ACCENT} />
          <button
            className="kb-btn"
            style={{ background: ACCENT, color: "#020810" }}
            onClick={handleSubmit}
            disabled={loading || !file}
          >
            {loading
              ? <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> Actualizando...</>
              : <><ShieldCheck size={13} /> Actualizar Reglas</>}
          </button>
        </div>
        <div className="kb-right">
          <ItemsList
            items={items}
            loading={loadingItems}
            onRefresh={fetchItems}
            renderItem={(item) => {
              const esSeccion = item.metadata?.tipo === "seccion" || item.document?.startsWith("#");
              return (
                <div className="kb-it-top" style={{ alignItems: "flex-start" }}>
                  {esSeccion
                    ? <Hash size={11} style={{ color: ACCENT, marginTop: 2, flexShrink: 0 }} />
                    : <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--t3)",
                        flexShrink: 0, marginTop: 4 }} />}
                  <span style={{ fontSize: 11, color: esSeccion ? "var(--t1)" : "var(--t2)",
                    fontWeight: esSeccion ? 600 : 400, lineHeight: 1.5 }}>
                    {item.document?.replace(/^#+\s*/, "").replace(/^-\s*/, "") ?? ""}
                  </span>
                  {item.metadata?.categoria && (
                    <Badge label={item.metadata.categoria}
                      bg={`color-mix(in srgb,${ACCENT} 12%,transparent)`} color={ACCENT} />
                  )}
                </div>
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   PAGE — Ingestion
══════════════════════════════════════════════════════ */
export default function Ingestion() {
  const [toast, setToast] = useState(null);
  const notify = useCallback((msg, type = "ok") => setToast({ msg, type }), []);

  return (
    <div className="kb kb-root">
      <style dangerouslySetInnerHTML={{ __html: SHEET }} />

      {/* Header */}
      <div className="kb-hd">
        <div className="kb-hd-icon">
          <Database size={20} />
        </div>
        <div>
          <h1>Entreanamiento</h1>
          <p>Inserta la información de tu empresa y actualiza sus reglas</p>
        </div>
        <div className="kb-hd-tag">
          {API_URL.replace(/^https?:\/\//, "")}
        </div>
      </div>

      {/* Cards */}
      <div className="kb-cards">
        <TodoCard       notify={notify} />  
        <EmpresaCard    notify={notify} />
        <ProductosCard  notify={notify} />
        <FaqCard        notify={notify} />
        <PromocionesCard notify={notify} />
        <ReglasCard     notify={notify} />
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          msg={toast.msg}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
